// src/pages/healthcare/consultation/consultationComponents/SpeakerAssignPopover.tsx
/**
 * Name one speaker in an in-person consult: tap the chip on any segment, pick the practitioner,
 * the patient or someone else, and every segment carrying that speaker label — the ones already on
 * screen and the ones still to come — takes the name.
 *
 * S25 of ClickUp 86d3zmpwv.
 *
 * IN-PERSON ONLY, and the gate is the presence of `onAssign` at the call site: online consults
 * already know whose microphone is whose (the room roster names them), so there is nothing to
 * assign. `TranscriptList` renders plain text when no handler is passed.
 *
 * The popover proposes; talk decides. The page emits `assignSpeaker` and the whole map comes back
 * as `speakerMapUpdated`, so two practitioner tabs converge instead of diverging.
 */
import React, { useState } from "react";
import { Button, Divider, Input, Popover, Radio, Space, Typography } from "antd";

import { SpeakerAssignment, SpeakerParticipantInput, SpeakerRole } from "../../../../types/appointments/ConsultTypes";
import { SPEAKER_DISPLAY_NAME_MAX_LENGTH, prettifySpeakerLabel } from "../../../../utils/consult/speakerNames";

const { Text } = Typography;

interface SpeakerAssignPopoverProps {
	/** The label being named — a diarisation label, or the participant id when there is none. */
	speakerLabel: string;
	/** What this label is currently bound to, if anything. Drives the Clear action. */
	assignment: SpeakerAssignment | null;
	practitionerName: string;
	patientName: string;
	/** `null` clears the label. Named, never spread — see `SpeakerParticipantInput`. */
	onAssign: (speakerLabel: string, participant: SpeakerParticipantInput | null) => void;
	/** The chip this popover hangs off. */
	children: React.ReactElement;
}

const SpeakerAssignPopover: React.FC<SpeakerAssignPopoverProps> = ({ speakerLabel, assignment, practitionerName, patientName, onAssign, children }) => {
	const [open, setOpen] = useState<boolean>(false);
	const [otherName, setOtherName] = useState<string>("");
	const [otherRole, setOtherRole] = useState<SpeakerRole>("other");

	const assign = (participant: SpeakerParticipantInput | null) => {
		setOpen(false);
		setOtherName("");
		setOtherRole("other");
		onAssign(speakerLabel, participant);
	};

	// `participantId` stays null: naming a diarisation label says who spoke, NOT which microphone
	// carried them — in person one microphone carries everybody. talk fills the field in when
	// voice enrollment can answer it (`source: 'enrollment'`).
	const assignNamed = (displayName: string, role: SpeakerRole) => assign({ participantId: null, displayName, role });

	const trimmedOther = otherName.trim();

	const content = (
		<div data-testid="consultation-speaker-picker" style={{ width: 240 }}>
			<Text type="secondary" style={{ fontSize: 11 }}>
				Who is {prettifySpeakerLabel(speakerLabel)}?
			</Text>

			<Space direction="vertical" size={4} style={{ width: "100%", marginTop: 8 }}>
				<Button block size="small" data-testid="consultation-speaker-option-practitioner" onClick={() => assignNamed(practitionerName, "practitioner")}>
					{practitionerName}
				</Button>
				<Button block size="small" data-testid="consultation-speaker-option-patient" onClick={() => assignNamed(patientName, "patient")}>
					{patientName}
				</Button>
			</Space>

			<Divider style={{ margin: "8px 0" }} />

			<Space direction="vertical" size={4} style={{ width: "100%" }}>
				<Input
					size="small"
					placeholder="Other person"
					aria-label="Other person"
					data-testid="consultation-speaker-other-name"
					value={otherName}
					maxLength={SPEAKER_DISPLAY_NAME_MAX_LENGTH}
					onChange={(e) => setOtherName(e.target.value)}
					onPressEnter={() => trimmedOther && assignNamed(trimmedOther, otherRole)}
				/>
				<Radio.Group size="small" value={otherRole} onChange={(e) => setOtherRole(e.target.value as SpeakerRole)} data-testid="consultation-speaker-other-role">
					<Radio.Button value="practitioner">Clinician</Radio.Button>
					<Radio.Button value="patient">Patient</Radio.Button>
					<Radio.Button value="other">Other</Radio.Button>
				</Radio.Group>
				<Button block size="small" type="primary" disabled={!trimmedOther} data-testid="consultation-speaker-other-save" onClick={() => assignNamed(trimmedOther, otherRole)}>
					Save name
				</Button>
			</Space>

			{assignment && (
				<>
					<Divider style={{ margin: "8px 0" }} />
					<Button block size="small" danger data-testid="consultation-speaker-clear" onClick={() => assign(null)}>
						Clear name
					</Button>
				</>
			)}
		</div>
	);

	return (
		<Popover content={content} title="Name this speaker" trigger="click" placement="topLeft" open={open} onOpenChange={setOpen} destroyTooltipOnHide>
			{children}
		</Popover>
	);
};

export default SpeakerAssignPopover;
